import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Stack,
  Alert,
  CircularProgress,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  Divider,
  Card,
  CardContent,
  Menu,
  Badge,
  Avatar,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Send as SendIcon,
  Clear as ClearIcon,
  History as HistoryIcon,
  ContentCopy as CopyIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  AutoAwesome as AutoAwesomeIcon
} from '@mui/icons-material';
import { urbanAgentService } from '../services/urbanAgentService';
import { 
  URBAN_AGENTS, 
  UrbanAgentType, 
  UrbanAgentSession,
  UrbanAgentIteration 
} from '../models/urbanAgents';

const eras = ['1970s', '1980s', '1990s', '2000s', '2010s', '2020s', 'Contemporary'];
const regions = ['NYC', 'LA', 'Chicago', 'London', 'Paris', 'Tokyo', 'Berlin', 'Global'];
const styles = {
  animation: ['Anime', 'Cartoon', 'Realistic', 'Abstract', 'Graffiti-inspired'],
  documentary: ['Photojournalistic', 'Street Photography', 'Portrait', 'Environmental'],
  music: ['Hip-Hop', 'Jazz', 'Electronic', 'Rock', 'Soul', 'Funk'],
  streetart: ['Graffiti', 'Mural', 'Stencil', 'Wheat Paste', 'Installation'],
  general: ['Mixed', 'Traditional', 'Modern', 'Experimental']
};

const UrbanAgentIterator: React.FC = () => {
  const { agentType } = useParams<{ agentType: string }>();
  const navigate = useNavigate();
  
  // Responsive hooks
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [prompt, setPrompt] = useState('');
  const [selectedEra, setSelectedEra] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('');
  const [selectedStyle, setSelectedStyle] = useState('');
  const [temperature, setTemperature] = useState(0.7);
  const [maxTokens, setMaxTokens] = useState(1024);
  const [currentSession, setCurrentSession] = useState<UrbanAgentSession | null>(null);
  const [feedback, setFeedback] = useState('');
  const [sessionMenuAnchor, setSessionMenuAnchor] = useState<null | HTMLElement>(null);
  const [savedSessions, setSavedSessions] = useState<string[]>([]);

  const agentConfig = URBAN_AGENTS.find(a => a.id === agentType);
  const agentStyles = styles[agentType as keyof typeof styles] || [];

  useEffect(() => {
    loadSavedSessions();
  }, []);

  const loadSavedSessions = () => {
    const sessionIds = urbanAgentService.getAllSessionIds();
    const filteredSessions = sessionIds.filter(id => {
      const session = urbanAgentService.getSessionFromLocalStorage(id);
      return session?.agentType === agentType;
    });
    setSavedSessions(filteredSessions);
  };

  const handleStartSession = async () => {
    if (!prompt.trim()) {
      setError('Please provide a prompt');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let response: UrbanAgentSession;
      const request = {
        prompt,
        style: selectedStyle || undefined,
        era: selectedEra || undefined,
        region: selectedRegion || undefined,
        maxResponseTokens: maxTokens,
        temperature
      };

      switch (agentType) {
        case UrbanAgentType.Animation:
          response = await urbanAgentService.startAnimationSession(request);
          break;
        case UrbanAgentType.Documentary:
          response = await urbanAgentService.startDocumentarySession(request);
          break;
        case UrbanAgentType.Music:
          response = await urbanAgentService.startMusicSession(request);
          break;
        case UrbanAgentType.StreetArt:
          response = await urbanAgentService.startStreetArtSession(request);
          break;
        default:
          response = await urbanAgentService.startGeneralSession(request);
      }

      setCurrentSession(response);
      urbanAgentService.saveSessionToLocalStorage(response.sessionId, response);
      loadSavedSessions();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to start session');
    } finally {
      setLoading(false);
    }
  };

  const handleIterate = async () => {
    if (!currentSession || !feedback.trim()) {
      setError('Please provide feedback for iteration');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const request = {
        feedback,
        style: selectedStyle || undefined,
        era: selectedEra || undefined,
        region: selectedRegion || undefined,
        maxResponseTokens: maxTokens,
        temperature
      };

      let response;
      switch (agentType) {
        case UrbanAgentType.Animation:
          response = await urbanAgentService.iterateAnimation(currentSession.sessionId, request);
          break;
        case UrbanAgentType.Documentary:
          response = await urbanAgentService.iterateDocumentary(currentSession.sessionId, request);
          break;
        case UrbanAgentType.Music:
          response = await urbanAgentService.iterateMusic(currentSession.sessionId, request);
          break;
        case UrbanAgentType.StreetArt:
          response = await urbanAgentService.iterateStreetArt(currentSession.sessionId, request);
          break;
        default:
          response = await urbanAgentService.iterateGeneral(currentSession.sessionId, request);
      }

      const newIteration: UrbanAgentIteration = {
        iterationNumber: response.iterationNumber,
        refinedPrompt: response.refinedPrompt,
        previousPrompt: response.previousPrompt || currentSession.currentPrompt,
        improvementExplanation: response.improvementExplanation,
        timestamp: new Date().toISOString()
      };

      const updatedSession = {
        ...currentSession,
        currentPrompt: response.refinedPrompt,
        iterations: [...currentSession.iterations, newIteration]
      };

      setCurrentSession(updatedSession);
      setFeedback('');
      urbanAgentService.saveSessionToLocalStorage(currentSession.sessionId, updatedSession);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to iterate');
    } finally {
      setLoading(false);
    }
  };

  const handleLoadSession = (sessionId: string) => {
    const session = urbanAgentService.getSessionFromLocalStorage(sessionId);
    if (session) {
      setCurrentSession(session);
      setPrompt(session.iterations[0]?.previousPrompt || '');
    }
    setSessionMenuAnchor(null);
  };

  const handleDeleteSession = (sessionId: string) => {
    urbanAgentService.deleteSessionFromLocalStorage(sessionId);
    loadSavedSessions();
    if (currentSession?.sessionId === sessionId) {
      setCurrentSession(null);
    }
  };

  const handleCopyPrompt = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const handleDownloadSession = () => {
    if (!currentSession) return;

    const content = JSON.stringify(currentSession, null, 2);
    const blob = new Blob([content], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `urban_${agentType}_session_${currentSession.sessionId}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (!agentConfig) {
    return (
      <Container>
        <Alert severity="error">Unknown agent type</Alert>
        <Button onClick={() => navigate('/urban-agents')}>Back to Gallery</Button>
      </Container>
    );
  }

  return (
    <Container 
      maxWidth="md" 
      sx={{ 
        py: { xs: 2, sm: 3, md: 4 },
        px: { xs: 1.5, sm: 2, md: 3 },
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden'
      }}
    >
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: { xs: 2, sm: 3, md: 4 } }}>
        <Typography 
          variant="h4" 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            gap: { xs: 0.5, sm: 1 }, 
            mb: { xs: 1, sm: 2 },
            fontSize: { xs: '1.5rem', sm: '1.875rem', md: '2.125rem' },
            flexWrap: 'wrap'
          }}
        >
          <Avatar 
            sx={{ 
              backgroundColor: agentConfig.color,
              width: { xs: 32, sm: 40 },
              height: { xs: 32, sm: 40 },
              fontSize: { xs: '18px', sm: '24px' }
            }}
          >
            {agentConfig.icon}
          </Avatar>
          {isMobile ? agentConfig.name.split(' ')[0] : agentConfig.name}
        </Typography>
        <Typography 
          variant="body1" 
          color="text.secondary"
          sx={{ 
            fontSize: { xs: '0.875rem', sm: '1rem' },
            px: { xs: 0.5, sm: 0 }
          }}
        >
          {agentConfig.description}
        </Typography>
      </Box>

      <Paper 
        elevation={0}
        sx={{ 
          border: 1,
          borderColor: 'divider',
          borderRadius: { xs: 1, sm: 2 },
          overflow: 'hidden',
          width: '100%'
        }}
      >
        <Box sx={{ 
          p: { xs: 1.5, sm: 3 },
          width: '100%',
          overflow: 'hidden'
        }}>
          <Stack 
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            spacing={2} 
            sx={{ mb: { xs: 2, sm: 3 } }}
          >
            <IconButton 
              onClick={() => navigate('/urban-agents')} 
              size="small"
              sx={{ 
                alignSelf: { xs: 'flex-start', sm: 'center' },
                minHeight: 44,
                minWidth: 44
              }}
            >
              <ArrowBackIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
            </IconButton>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Back to Gallery
              </Typography>
            </Box>
            
            <IconButton 
              onClick={(e) => setSessionMenuAnchor(e.currentTarget)}
              sx={{ minHeight: 44, minWidth: 44 }}
            >
              <Badge badgeContent={savedSessions.length} color="primary">
                <HistoryIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
              </Badge>
            </IconButton>
          </Stack>

          <Menu
          anchorEl={sessionMenuAnchor}
          open={Boolean(sessionMenuAnchor)}
          onClose={() => setSessionMenuAnchor(null)}
        >
          {savedSessions.length === 0 ? (
            <MenuItem disabled>
              <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                No saved sessions
              </Typography>
            </MenuItem>
          ) : (
            savedSessions.map((sessionId) => {
              const session = urbanAgentService.getSessionFromLocalStorage(sessionId);
              return (
                <MenuItem key={sessionId} sx={{ px: { xs: 1.5, sm: 2 } }}>
                  <Stack 
                    direction="row" 
                    spacing={2} 
                    alignItems="center" 
                    sx={{ width: '100%' }}
                  >
                    <Box 
                      sx={{ flexGrow: 1 }} 
                      onClick={() => handleLoadSession(sessionId)}
                    >
                      <Typography 
                        variant="body2" 
                        noWrap
                        sx={{ fontSize: { xs: '0.875rem', sm: '0.95rem' } }}
                      >
                        Session {sessionId.substring(0, isMobile ? 6 : 8)}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                      >
                        {new Date(session.lastUpdated).toLocaleDateString()}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteSession(sessionId);
                      }}
                      sx={{ minHeight: 44, minWidth: 44 }}
                    >
                      <DeleteIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                    </IconButton>
                  </Stack>
                </MenuItem>
              );
            })
          )}
        </Menu>

        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 2,
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }} 
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {!currentSession && (
          <>
            <TextField
              fullWidth
              multiline
              rows={isMobile ? 2 : 3}
              label="Initial Prompt"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder={`Describe the ${agentType} scene you want to create...`}
              sx={{ mb: 2 }}
              InputProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
              InputLabelProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
              <FormControl 
                fullWidth
                sx={{
                  '& .MuiInputLabel-root': { fontSize: { xs: '0.875rem', sm: '1rem' } },
                  '& .MuiSelect-select': { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
              >
                <InputLabel>Era</InputLabel>
                <Select
                  value={selectedEra}
                  onChange={(e) => setSelectedEra(e.target.value)}
                  label="Era"
                >
                  <MenuItem 
                    value="" 
                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                  >
                    Any Era
                  </MenuItem>
                  {eras.map(era => (
                    <MenuItem 
                      key={era} 
                      value={era}
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                    >
                      {era}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl 
                fullWidth
                sx={{
                  '& .MuiInputLabel-root': { fontSize: { xs: '0.875rem', sm: '1rem' } },
                  '& .MuiSelect-select': { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
              >
                <InputLabel>Region</InputLabel>
                <Select
                  value={selectedRegion}
                  onChange={(e) => setSelectedRegion(e.target.value)}
                  label="Region"
                >
                  <MenuItem 
                    value="" 
                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                  >
                    Any Region
                  </MenuItem>
                  {regions.map(region => (
                    <MenuItem 
                      key={region} 
                      value={region}
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                    >
                      {region}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl 
                fullWidth
                sx={{
                  '& .MuiInputLabel-root': { fontSize: { xs: '0.875rem', sm: '1rem' } },
                  '& .MuiSelect-select': { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
              >
                <InputLabel>Style</InputLabel>
                <Select
                  value={selectedStyle}
                  onChange={(e) => setSelectedStyle(e.target.value)}
                  label="Style"
                >
                  <MenuItem 
                    value="" 
                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                  >
                    Any Style
                  </MenuItem>
                  {agentStyles.map(style => (
                    <MenuItem 
                      key={style} 
                      value={style}
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                    >
                      {style}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            <Box sx={{ mb: 2, px: { xs: 0.5, sm: 0 } }}>
              <Typography 
                gutterBottom
                sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
              >
                Temperature: {temperature}
              </Typography>
              <Slider
                value={temperature}
                onChange={(_, value) => setTemperature(value as number)}
                min={0}
                max={1}
                step={0.1}
                marks
                valueLabelDisplay="auto"
              />
            </Box>

            <Button
              variant="contained"
              fullWidth
              size="large"
              onClick={handleStartSession}
              disabled={loading || !prompt.trim()}
              startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
              sx={{ 
                backgroundColor: agentConfig.color,
                minHeight: 44,
                fontSize: { xs: '0.875rem', sm: '1rem' },
                '&:hover': {
                  backgroundColor: agentConfig.color,
                  filter: 'brightness(0.9)'
                }
              }}
            >
              {loading ? 'Generating...' : 'Start Urban Culture Session'}
            </Button>
          </>
        )}
        </Box>
      </Paper>

      {currentSession && (
        <Paper 
          elevation={0}
          sx={{ 
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            overflow: 'hidden',
            mt: 3
          }}
        >
          <Box sx={{ p: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
            <Typography variant="h5">
              Session: {currentSession.sessionId.substring(0, 8)}
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Tooltip title="Download session">
                <IconButton 
                  onClick={handleDownloadSession}
                  sx={{ minHeight: 44, minWidth: 44 }}
                >
                  <DownloadIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                </IconButton>
              </Tooltip>
              <Button
                variant="outlined"
                fullWidth={isMobile}
                startIcon={<ClearIcon />}
                onClick={() => {
                  setCurrentSession(null);
                  setPrompt('');
                  setFeedback('');
                }}
                sx={{ 
                  minHeight: 44,
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }}
              >
                New Session
              </Button>
            </Stack>
          </Stack>

          <Box sx={{ mb: 3 }}>
            <Typography 
              variant="h6" 
              gutterBottom
              sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
            >
              Current Prompt
            </Typography>
            <Card variant="outlined">
              <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Stack 
                  direction={{ xs: 'column', sm: 'row' }} 
                  justifyContent="space-between" 
                  alignItems="start"
                  spacing={{ xs: 1, sm: 0 }}
                >
                  <Box sx={{ flexGrow: 1, width: { xs: '100%', sm: 'auto' } }}>
                    <ReactMarkdown
                      components={{
                        p: ({ children }) => (
                          <Typography 
                            variant="body1" 
                            component="div" 
                            sx={{ 
                              mb: 1,
                              fontSize: { xs: '0.875rem', sm: '1rem' }
                            }}
                          >
                            {children}
                          </Typography>
                        ),
                        h1: ({ children }) => (
                          <Typography 
                            variant="h6" 
                            component="div" 
                            sx={{ 
                              mb: 1, 
                              fontWeight: 600,
                              fontSize: { xs: '1.1rem', sm: '1.25rem' }
                            }}
                          >
                            {children}
                          </Typography>
                        ),
                        h2: ({ children }) => (
                          <Typography 
                            variant="subtitle1" 
                            component="div" 
                            sx={{ 
                              mb: 1, 
                              fontWeight: 600,
                              fontSize: { xs: '1rem', sm: '1.15rem' }
                            }}
                          >
                            {children}
                          </Typography>
                        ),
                        h3: ({ children }) => (
                          <Typography 
                            variant="subtitle2" 
                            component="div" 
                            sx={{ 
                              mb: 1, 
                              fontWeight: 600,
                              fontSize: { xs: '0.9rem', sm: '1rem' }
                            }}
                          >
                            {children}
                          </Typography>
                        ),
                        ul: ({ children }) => (
                          <Box component="ul" sx={{ m: 0, pl: { xs: 2, sm: 3 }, mb: 1 }}>
                            {children}
                          </Box>
                        ),
                        li: ({ children }) => (
                          <Typography 
                            component="li" 
                            variant="body1" 
                            sx={{ 
                              mb: 0.5,
                              fontSize: { xs: '0.875rem', sm: '1rem' }
                            }}
                          >
                            {children}
                          </Typography>
                        ),
                      }}
                    >
                      {currentSession.currentPrompt}
                    </ReactMarkdown>
                  </Box>
                  <Tooltip title="Copy prompt">
                    <IconButton 
                      size="small" 
                      onClick={() => handleCopyPrompt(currentSession.currentPrompt)}
                      sx={{ minHeight: 44, minWidth: 44 }}
                    >
                      <CopyIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </CardContent>
            </Card>
          </Box>

          {/* Display documentary references for documentary agent type */}
          {currentSession.agentType === UrbanAgentType.Documentary && 
           currentSession.metadata?.documentaryReferences && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Documentary References
              </Typography>
              <Card variant="outlined">
                <CardContent>
                  {Object.entries(currentSession.metadata.documentaryReferences).map(([key, value]) => (
                    <Box key={key} sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
                        {key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}
                      </Typography>
                      {Array.isArray(value) ? (
                        <Box component="ul" sx={{ m: 0, pl: 3 }}>
                          {value.map((item, index) => (
                            <Box component="li" key={index} sx={{ mb: 0.5 }}>
                              <ReactMarkdown
                                components={{
                                  p: ({ children }) => (
                                    <Typography variant="body2" component="span">
                                      {children}
                                    </Typography>
                                  ),
                                }}
                              >
                                {String(item)}
                              </ReactMarkdown>
                            </Box>
                          ))}
                        </Box>
                      ) : (
                        <ReactMarkdown
                          components={{
                            p: ({ children }) => (
                              <Typography variant="body2" component="div">
                                {children}
                              </Typography>
                            ),
                          }}
                        >
                          {String(value)}
                        </ReactMarkdown>
                      )}
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Box>
          )}

          {currentSession.iterations.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography 
                variant="h6" 
                gutterBottom
                sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
              >
                Iteration History
              </Typography>
              <Stack spacing={2}>
                {currentSession.iterations.map((iteration, index) => (
                  <Card key={index} variant="outlined">
                    <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                      <Stack 
                        direction={{ xs: 'column', sm: 'row' }} 
                        justifyContent="space-between" 
                        alignItems={{ xs: 'flex-start', sm: 'center' }} 
                        spacing={{ xs: 1, sm: 0 }}
                        sx={{ mb: 1 }}
                      >
                        <Chip 
                          label={`Iteration ${iteration.iterationNumber}`} 
                          size={isMobile ? 'small' : 'medium'}
                          color="primary"
                        />
                        <Typography variant="caption" color="text.secondary">
                          {new Date(iteration.timestamp).toLocaleString()}
                        </Typography>
                      </Stack>
                      {iteration.improvementExplanation && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                          {iteration.improvementExplanation}
                        </Alert>
                      )}
                      <ReactMarkdown
                        components={{
                          p: ({ children }) => (
                            <Typography 
                              variant="body2" 
                              component="div" 
                              sx={{ 
                                mb: 1,
                                fontSize: { xs: '0.875rem', sm: '0.95rem' }
                              }}
                            >
                              {children}
                            </Typography>
                          ),
                          h1: ({ children }) => (
                            <Typography 
                              variant="subtitle1" 
                              component="div" 
                              sx={{ 
                                mb: 1, 
                                fontWeight: 600,
                                fontSize: { xs: '1rem', sm: '1.15rem' }
                              }}
                            >
                              {children}
                            </Typography>
                          ),
                          h2: ({ children }) => (
                            <Typography 
                              variant="subtitle2" 
                              component="div" 
                              sx={{ 
                                mb: 1, 
                                fontWeight: 600,
                                fontSize: { xs: '0.9rem', sm: '1rem' }
                              }}
                            >
                              {children}
                            </Typography>
                          ),
                          ul: ({ children }) => (
                            <Box component="ul" sx={{ m: 0, pl: { xs: 2, sm: 3 }, mb: 1 }}>
                              {children}
                            </Box>
                          ),
                          li: ({ children }) => (
                            <Typography 
                              component="li" 
                              variant="body2" 
                              sx={{ 
                                mb: 0.5,
                                fontSize: { xs: '0.875rem', sm: '0.95rem' }
                              }}
                            >
                              {children}
                            </Typography>
                          ),
                        }}
                      >
                        {iteration.refinedPrompt}
                      </ReactMarkdown>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </Box>
          )}

          <Divider sx={{ my: 3 }} />

          <Box>
            <Typography 
              variant="h6" 
              gutterBottom
              sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
            >
              Continue Refining
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={isMobile ? 2 : 3}
              label="Feedback or Direction"
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="What would you like to adjust or improve?"
              sx={{ mb: 2 }}
              InputProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
              InputLabelProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
            />
            <Button
              variant="contained"
              fullWidth
              onClick={handleIterate}
              disabled={loading || !feedback.trim()}
              startIcon={loading ? <CircularProgress size={20} /> : <RefreshIcon />}
              sx={{ 
                backgroundColor: agentConfig.color,
                minHeight: 44,
                fontSize: { xs: '0.875rem', sm: '1rem' },
                '&:hover': {
                  backgroundColor: agentConfig.color,
                  filter: 'brightness(0.9)'
                }
              }}
            >
              {loading ? 'Refining...' : 'Refine Prompt'}
            </Button>
          </Box>
        </Box>
        </Paper>
      )}
    </Container>
  );
};

export default UrbanAgentIterator;